import { useCallback, useState } from 'react';

import type CardClient from '@atlaskit/link-provider/client';

import type { ManagedBlockSourceMode } from '../../../common/agentManagedExtensionPluginType';
import type { ManagedBlockConfigurationUpdate } from '../configuration';

import type { AiManagedObjectSourceEntry } from '@atlassian/ai-managed-objects/sources';
import { useResolveSources } from './useResolveSources';
import { useSourceInput } from './useSourceInput';

type UseSourcesArgs = {
	cardClient: CardClient;
	onChange: (update: ManagedBlockConfigurationUpdate) => void;
	onConfigEdit: () => void;
	onInteraction: () => void;
	showInitialSourceDisclaimer?: boolean;
	sourceEntries: AiManagedObjectSourceEntry[];
	sourceMode: ManagedBlockSourceMode;
	sourceUrls: string;
};

type UseSourcesResult = ReturnType<typeof useSourceInput> & {
	handleDismissSourceDisclaimer: () => void;
	isSourceDisclaimerVisible: boolean;
	manualSources: AiManagedObjectSourceEntry[];
};

export const useSources = ({
	cardClient,
	onChange,
	onConfigEdit,
	onInteraction,
	showInitialSourceDisclaimer = false,
	sourceEntries,
	sourceMode,
	sourceUrls,
}: UseSourcesArgs): UseSourcesResult => {
	const [isSourceDisclaimerDismissed, setIsSourceDisclaimerDismissed] = useState(false);
	const [isSourceDisclaimerVisible, setIsSourceDisclaimerVisible] = useState(
		() => showInitialSourceDisclaimer,
	);

	useResolveSources({
		cardClient,
		onChange,
		sourceEntries,
	});

	const handleSourcesAdded = useCallback(() => {
		if (!isSourceDisclaimerDismissed) {
			setIsSourceDisclaimerVisible(true);
		}
	}, [isSourceDisclaimerDismissed]);

	const sourceInput = useSourceInput({
		onChange,
		onConfigEdit,
		onInteraction,
		onSourcesAdded: handleSourcesAdded,
		sourceEntries,
		sourceMode,
		sourceUrls,
	});

	const handleDismissSourceDisclaimer = useCallback(() => {
		setIsSourceDisclaimerDismissed(true);
		setIsSourceDisclaimerVisible(false);
	}, []);

	return {
		handleDismissSourceDisclaimer,
		isSourceDisclaimerVisible,
		manualSources: sourceEntries,
		...sourceInput,
	};
};
